/**
 * Profile setup — pure helpers + the "already asked" mark.
 *
 * `profileGaps` and `suggestUsername` are ports of `app/src/utils/edit-profile.ts`
 * (not reachable through `@shared/*`, which only covers `app/src/data/`). Keep
 * them in step with the app source.
 */
import type { OwnProfile } from '@shared/types';

import { USERNAME_PATTERN } from '@/features/profile/api';
import { socialEntries } from '@/features/profile/socials';

export type ProfileGap = 'photo' | 'name' | 'username' | 'bio';
/** `socials` is web-only so far: offered last, never a reason to open the prompt. */
export type SetupStep = 'language' | ProfileGap | 'socials';

/** What the setup still asks for; empty = nothing to prompt (app: `profileGaps`). */
export function profileGaps(
  p: Pick<OwnProfile, 'avatarUrl' | 'displayName' | 'usernameAutoAssigned' | 'bio'>,
): ProfileGap[] {
  const gaps: ProfileGap[] = [];
  if (!p.avatarUrl) gaps.push('photo');
  if (!p.displayName?.trim()) gaps.push('name');
  if (p.usernameAutoAssigned) gaps.push('username');
  if (!p.bio?.trim()) gaps.push('bio');
  return gaps;
}

/** Language first, then only the gaps (the app's order), then socials when there are none. */
export function setupSteps(
  p: Parameters<typeof profileGaps>[0] & Pick<OwnProfile, 'socials'>,
): SetupStep[] {
  const steps: SetupStep[] = ['language', ...profileGaps(p)];
  if (socialEntries(p.socials).length === 0) steps.push('socials');
  return steps;
}

export function normalizeUsername(value: string): string {
  return value.trim().toLowerCase();
}

/** A valid-shaped username guess from a display name ("João Silva" → "joao_silva"), or ''. */
export function suggestUsername(name: string): string {
  const handle = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 20);
  return USERNAME_PATTERN.test(handle) ? handle : '';
}

/*
 * Once per account per device. Bump the version to ask everyone again after a
 * material change to the flow. An in-memory set backs the mark up when
 * storage is unavailable (private mode), so it still asks at most once per tab.
 */
export const PROFILE_SETUP_VERSION = 1;
export const profileSetupKey = (sub: string) =>
  `screenatlas.profileSetup.v${PROFILE_SETUP_VERSION}.${sub}`;

const askedThisSession = new Set<string>();

export function wasProfileSetupShown(sub: string): boolean {
  if (askedThisSession.has(sub)) return true;
  try {
    return window.localStorage.getItem(profileSetupKey(sub)) !== null;
  } catch {
    return false;
  }
}

export function markProfileSetupShown(sub: string): void {
  askedThisSession.add(sub);
  try {
    window.localStorage.setItem(profileSetupKey(sub), new Date().toISOString());
  } catch {
    // non-fatal: the in-memory mark still holds for this tab
  }
}

/** Test seam: forget the in-memory marks (localStorage is cleared per test). */
export function resetProfileSetupMemory(): void {
  askedThisSession.clear();
}
